import { fetchOfferPageContextProps } from '@portal/pages/api/offer';
import { getCustomComponent } from '@portal/processes/Content/contentBlockComponentsMap';
import type { CustomContent } from '@portal/processes/DynamicLanders/types';
import { PageContextProps } from '@portal/processes/Page/types';
import { FULL_LOCALE_TO_OFFER_LOCAL_MAP } from '@portal/schema/offer';
import { TranslationProvider } from '@portal/shared/providers/TranslationProvider/TranslationProvider';
import {
  getOfferInvalidReason,
  getStoreCodeFromAtlasLocale,
  isOfferInvalidReason404,
  isSupportedCheckoutFlowType
} from '@portal/shared/utils/offer/generic';
import { type GetServerSideProps, type InferGetServerSidePropsType } from 'next';
import Head from 'next/head';
import { PageProvider } from 'processes/Page/PageContext';
import { useMemo } from 'react';
import { getSafeLocale } from 'shared/utils/locale';

export const getServerSideProps = (async ({
  locale: localeProp = 'en-us',
  params: { slug: slugProp = '', id: idProp = '' } = {}
}) => {
  const locale = getSafeLocale(localeProp);
  const checkoutFlowUnsafe = Array.isArray(slugProp) ? slugProp[0] || '' : slugProp;
  if (!isSupportedCheckoutFlowType(checkoutFlowUnsafe)) {
    // eslint-disable-next-line no-console
    console.error('Unsupported checkout flow type:', checkoutFlowUnsafe);
    return { notFound: true };
  }

  const idString = Array.isArray(idProp) ? idProp[0] || '' : idProp;
  const idUnsafe = idString && /^[0-9]+$/.test(idString) ? parseInt(idString, 10) : null;
  if (!idUnsafe || Number.isNaN(idUnsafe)) {
    // eslint-disable-next-line no-console
    console.error('Invalid offer id provided:', idString);
    return { notFound: true };
  }
  const checkoutFlow = checkoutFlowUnsafe;
  const offerId = idUnsafe;

  const offerPageContextProps = await fetchOfferPageContextProps(offerId, locale);
  const { offer, shopifyVariantsByStoreCode, coreProducts, baseOfferCoreProducts } =
    offerPageContextProps;

  const store = getStoreCodeFromAtlasLocale(FULL_LOCALE_TO_OFFER_LOCAL_MAP[locale]);
  if (!offer || !coreProducts?.length || !shopifyVariantsByStoreCode?.[store]?.length) {
    // eslint-disable-next-line no-console
    console.error('No core products or shopify variants found for offer:', offerId);
    return { notFound: true };
  }

  const offerInvalidReason = getOfferInvalidReason(offer, locale);
  if (offerInvalidReason && isOfferInvalidReason404(offerInvalidReason)) {
    // eslint-disable-next-line no-console
    console.error('Invalid offer', offerInvalidReason);
    return { notFound: true };
  }

  const pageContextProps = {
    page: {},
    checkoutFlow: {
      type: checkoutFlow
    },
    offerId,
    ...offerPageContextProps,
    offer,
    shopifyVariantsByStoreCode,
    coreProducts,
    baseOfferCoreProducts,
    offerInvalidReason,
    sectionsUsed: {
      offerAnchor: false,
      // Treat checkout flow offers as builder offers.
      builderOffer: true,
      sanityOffer: false
    }
  } satisfies PageContextProps;

  return {
    props: {
      pageContextProps,
      componentSlug: `offer-builder-${checkoutFlow}` as const,
      locale,
      store,
      offerInvalidReason
    }
  };
}) satisfies GetServerSideProps;

type LanderProps = InferGetServerSidePropsType<typeof getServerSideProps>;

// NOTE: Based on `portal/src/processes/Content/ContentPage.tsx` and stripped
// down to support only an OfferBuilder checkout.
const OfferBuilderLander = ({ pageContextProps, componentSlug }: LanderProps) => {
  // Cache an empty array so we can satisfy the ContentBlock props.
  const customContent = useMemo(() => [] as CustomContent[], []);

  const Component = useMemo(() => getCustomComponent(componentSlug), [componentSlug]);

  return (
    <TranslationProvider>
      <PageProvider {...pageContextProps}>
        <Head>
          <title>AG1 by Athletic Greens® - Official Site</title>
          <meta property="og:type" content="website" />
          <meta property="og:title" content="AG1 by Athletic Greens® - Official Site" />
        </Head>
        <main data-content="main">
          <Component customContent={customContent} slug={componentSlug} visibleOnPageLoad />
        </main>
      </PageProvider>
    </TranslationProvider>
  );
};

export default OfferBuilderLander;
